import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSettings } from './SettingsContext';

const TimerContext = createContext();

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const { getBookingSettings } = useSettings();
  const [bookingTimeout, setBookingTimeout] = useState(15); // Default 15 minutes
  const [timeRemaining, setTimeRemaining] = useState(bookingTimeout * 60); // Convert to seconds
  const [isActive, setIsActive] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Load booking timeout from settings
  const loadSettings = useCallback(async () => {
    try {
      const settings = await getBookingSettings();
      const timeout = settings.bookingTimeout || 15;
      setBookingTimeout(timeout);
      setTimeRemaining(timeout * 60);
    } catch (error) {
      console.error('Error loading booking timeout:', error);
      // Keep default value
    }
  }, [getBookingSettings]);

  // Load booking timeout from settings
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Listen for admin settings updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_settings_updated') {
        // console.log('Admin settings updated, refreshing timer settings...');
        loadSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Also check for admin updates on page focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const lastAdminUpdate = localStorage.getItem('admin_settings_updated');
        const lastUpdate = localStorage.getItem('last_timer_settings_update');
        
        if (lastAdminUpdate && (!lastUpdate || parseInt(lastAdminUpdate) > parseInt(lastUpdate))) {
          // console.log('Page focused, admin settings were updated, refreshing timer settings...');
          loadSettings();
          localStorage.setItem('last_timer_settings_update', Date.now().toString());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    let interval = null;

    if (isActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          if (time <= 1) {
            setIsActive(false);
            setIsExpired(true);
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (!isActive && timeRemaining !== 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isActive, timeRemaining]);

  const startTimer = () => {
    setIsActive(true);
    setIsExpired(false);
  };

  const stopTimer = () => {
    setIsActive(false);
  };

  const resetTimer = () => {
    setTimeRemaining(bookingTimeout * 60);
    setIsActive(false);
    setIsExpired(false);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const value = {
    timeRemaining,
    isActive,
    isExpired,
    startTimer,
    stopTimer,
    resetTimer,
    formatTime,
    formattedTime: formatTime(timeRemaining)
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};
